import { useState } from 'react'
import { FileUp } from 'lucide-react'

import { BottomPanel } from '@/components/layout/bottom-panel'
import { Sidebar } from '@/components/layout/sidebar'
import { SidebarDrawer } from '@/components/layout/sidebar-drawer'
import { Toolbar } from '@/components/layout/toolbar'
import { Workspace } from '@/components/layout/workspace'
import { useFileImport } from '@/hooks/use-file-import'
import { useUndoRedoShortcuts } from '@/hooks/use-undo-redo-shortcuts'
import { useViewportSize } from '@/hooks/use-viewport-size'
import { useWindowFileDrop } from '@/hooks/use-window-file-drop'
import { MD_BREAKPOINT_PX } from '@/lib/timeline-panel-size'

/**
 * The toolbar / sidebar / workspace / bottom-panel layout. Responsive:
 * below `md` the sidebar moves into a slide-in drawer (`SidebarDrawer`,
 * opened from the toolbar) instead of taking a slice of a phone's height,
 * so the workspace and timeline get the screen. Sized to the dynamic
 * viewport height (`dvh`), so mobile browser bars never cover the timeline.
 *
 * The whole window is the drop target for FIT files
 * (`useWindowFileDrop`); the drop overlay is `position: fixed` over the
 * viewport, so the highlighted area is exactly the area that accepts the
 * drop, regardless of scrolling or which panel the pointer is over.
 */
export function AppShell() {
  useUndoRedoShortcuts()
  const importFiles = useFileImport()
  const isDragActive = useWindowFileDrop({ onFiles: (files) => void importFiles(files) })
  const isNarrow = useViewportSize().width < MD_BREAKPOINT_PX
  const [drawerOpen, setDrawerOpen] = useState(false)

  return (
    <div className="bg-background text-foreground flex h-dvh flex-col">
      <Toolbar onShowActivities={isNarrow ? () => setDrawerOpen(true) : undefined} />
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {isNarrow ? <SidebarDrawer open={drawerOpen} onOpenChange={setDrawerOpen} /> : <Sidebar />}
        <Workspace />
      </div>
      <BottomPanel />
      {isDragActive && <FileDropOverlay />}
    </div>
  )
}

function FileDropOverlay() {
  return (
    <div
      data-testid="file-drop-overlay"
      className="border-primary bg-background/90 text-primary pointer-events-none fixed inset-4 z-[60] flex items-center justify-center gap-2 rounded-lg border-2 border-dashed text-sm font-medium"
      aria-hidden="true"
    >
      <FileUp className="h-5 w-5" />
      Drop FIT files to import
    </div>
  )
}
